// context.ts — the locale context and its hooks. No component export lives here, so
// `react-refresh/only-export-components` has nothing to complain about and fast refresh keeps
// working for `LocaleProvider.tsx`.
//
// The context DEFAULT is the real English catalog, not a throwing stub: component tests render
// subtrees with no provider (see `WorkspaceMenu.test.tsx`, `Onboarding.test.tsx`, …) and must
// keep rendering today's English byte-for-byte. A missing provider is therefore not an error —
// it is English, which is also the product default.
import { createContext, useContext } from 'react';
import { en, type Catalog } from './en';
import type { Locale, LocalePreference } from './index';

export interface LocaleContextValue {
  /** The resolved locale — never 'system'. */
  locale: Locale;
  /** What the user chose, including 'system'. The switcher renders THIS. */
  preference: LocalePreference;
  t: Catalog;
  setPreference: (pref: LocalePreference) => void;
}

export const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  preference: 'system',
  t: en,
  setPreference: () => {},
});

/** The active catalog. The hook 95% of the app uses. */
export function useT(): Catalog {
  return useContext(LocaleContext).t;
}

/** The full context — for `Intl` formatting (`locale`) and the switcher (`preference`/`setPreference`). */
export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}
